import {
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
  } from "@mui/material";
  import lscache from "lscache";
  import React, { ChangeEvent, SyntheticEvent, useEffect } from "react";
  import { FormattedMessage, useIntl } from "react-intl";
  import { useCouchsurfingFunctions } from "../../hooks/couchsurfing/useCouchsurfingFunctions";
  import ICouchsurfing from "../../types/couchsurfing-type"
  import { accommodationTypes } from "../../utils/accommodationTypes";
  import AddressFields from "../Forms/Address";
  import TitleDescription from "../Forms/TitleDescription";
  import GroupedCountries, {
  } from "../Handlers/CountryHandler";
  import PhotoHandler from "../Handlers/PhotoHandler";
  
  interface CouchsurfingFormProps {
    editing: boolean;
    formData: ICouchsurfing;
    handleSubmit: (e: React.FormEvent) => void;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
  }
  
  const CouchsurfingForm: React.FC<CouchsurfingFormProps> = ({
    editing,
    formData,
    setFormData,
    handleSubmit,
  }) => {
    const intl = useIntl();
    const { TranslatedCouchsurfingOptions } = useCouchsurfingFunctions();
    const translatedCouchsurfingOptions =
      TranslatedCouchsurfingOptions(accommodationTypes);
  
    useEffect(() => {
      if (formData.photos.length > 0) {
        lscache.set("couchsurfing-photos", formData.photos, 30);
      }
    }, [formData.photos]);
  
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value === null) {
        return;
      }
      setFormData({ ...formData, [name]: value });
      lscache.set("couchsurfing-" + name, value, 30);
    };
  
    const handleTypeChange = (event: SelectChangeEvent<string>) => {
      const { name, value } = event.target;
      if (value === null) {
        return;
      }
      setFormData({ ...formData, [name]: value });
      lscache.set("couchsurfing-" + name, value, 30);
    };
  
    const handleCountryChange = (
      event: SyntheticEvent,
      newValue: string | null,
      newCurrency: string | null
    ) => {
      setFormData((prevState: string[]) => ({
        ...prevState,
        country: newValue || "",
        currency: newCurrency || "",
      }));
      lscache.set("couchsurfing-country", newValue, 30);
      lscache.set("couchsurfing-currency", newCurrency, 30);
    };
  
    return (
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TitleDescription
              title={formData.title}
              description={formData.description}
              handleChange={handleChange}
              fieldName="title"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>
                <FormattedMessage id="accomodation-type" />
              </InputLabel>
              <Select
                name="accommodationType"
                value={formData.accommodationType || "couchsurfing"}
                onChange={handleTypeChange}
              >
                {translatedCouchsurfingOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <FormattedMessage id={type.labelId} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl component="fieldset" style={{ display: "flex" }}>
              <FormLabel
                component="legend"
                style={{ height: "0px", fontSize: 10 }}
              >
                <FormattedMessage id="gender" />
              </FormLabel>
              <RadioGroup
                name="genderPreference"
                row
                value={formData.genderPreference || "any"}
                onChange={handleTypeChange}
                style={{ display: "flex" }}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "male",
                    defaultMessage: "Male",
                  })}
                />
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "female",
                    defaultMessage: "Female",
                  })}
                />
                <FormControlLabel
                  defaultChecked
                  value="any"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "any",
                    defaultMessage: "any",
                  })}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={9}>
            <AddressFields
              address={formData.address}
              city={formData.city}
              handleChange={handleChange}
            />
          </Grid>
  
          <Grid item xs={12} md={6} lg={3}>
            <GroupedCountries
              country={formData.country!}
              handleCountryChange={handleCountryChange}
            />
          </Grid>
          <Grid item xs={12}>
            <PhotoHandler
              initialPhotos={formData.photos || []}
              onPhotosChange={(newPhotos) =>
                setFormData({ ...formData, photos: newPhotos })
              }
              numberOfPhotos={formData.photos?.length || 0}
              source={formData.listingType}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} mb={2}>
            <Button variant="contained" type="submit" fullWidth>
              {editing ? (
                <FormattedMessage id="update-couchsurfing" />
              ) : (
                <FormattedMessage id="submit-couchsurfing" />
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  };
  
  export default CouchsurfingForm;
  